/**
 * FanVote25 brand asset registry.
 *
 * ========================= HOW TO ADD YOUR BRAND =========================
 *
 * Drop files into  web/src/assets/brand/  with these names:
 *
 *   logo.svg        full horizontal logo (top nav + footer)
 *   logo-mark.svg   square mark (compact spots; also used as favicon fallback)
 *   favicon.svg     browser tab icon
 *
 * .svg, .png, .webp, .jpg and .ico all work; SVG wins when both exist.
 * No code changes - the nav swaps off the text wordmark and the favicon is
 * applied at startup. See web/src/assets/brand/README.md for specs.
 * =========================================================================
 */

const assets = import.meta.glob('../assets/brand/*.{svg,png,webp,jpg,jpeg,ico}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const EXT_PRIORITY = ['svg', 'png', 'webp', 'jpg', 'jpeg', 'ico'];

function find(baseName: string): string | null {
  for (const ext of EXT_PRIORITY) {
    const hit = Object.entries(assets).find(([path]) => path.endsWith(`/${baseName}.${ext}`));
    if (hit) return hit[1];
  }
  return null;
}

/** Full horizontal lockup. Null means the built-in text wordmark renders. */
export const BRAND_LOGO = find('logo');

/** Square mark for tight spots; falls back to the full logo. */
export const BRAND_MARK = find('logo-mark') ?? BRAND_LOGO;

/** Browser tab icon; a square mark is a fine favicon if none is supplied. */
export const BRAND_FAVICON = find('favicon') ?? find('logo-mark');

function mimeFor(url: string): string {
  if (url.includes('.svg')) return 'image/svg+xml';
  if (url.includes('.ico')) return 'image/x-icon';
  return 'image/png';
}

/**
 * Swaps the favicon to the dropped-in file. Runs once at boot; a no-op until a
 * favicon.* or logo-mark.* file exists in the brand folder.
 */
export function applyBrandAssets(): void {
  if (!BRAND_FAVICON) return;
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = BRAND_FAVICON;
  link.type = mimeFor(BRAND_FAVICON);

  // iOS home-screen icon prefers a raster mark when one was supplied.
  const touch = find('icon-192') ?? find('logo-mark');
  if (touch && !touch.includes('.svg')) {
    let apple = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.href = touch;
  }
}
